import { DateTime } from 'luxon'

/**
 * Pure helpers behind entity peeks — the side panel that opens when you click a
 * related record (a meter's location, a location's meters) anywhere in the app.
 * No React, so they're covered by `node:test` (relations.test.js).
 */

/** The query-string key an open peek lives under: `?peek=location:<id>`. */
export const PEEK_PARAM = 'peek'

/** The fuels a meter peek knows about, keyed by entity type. */
export const METER_TYPES = {
    'electricity-meter': { fuel: 'electricity', label: 'Electricity meter' },
    'gas-meter': { fuel: 'gas', label: 'Gas meter' },
    'water-meter': { fuel: 'water', label: 'Water meter' }
}

export function formatPeek(type, id) {
    return `${type}:${id}`
}

/** `'location:abc'` → `{ type: 'location', id: 'abc' }`; anything malformed → null. */
export function parsePeek(value) {
    if (typeof value !== 'string') return null
    const separator = value.indexOf(':')
    if (separator <= 0 || separator === value.length - 1) return null
    return { type: value.slice(0, separator), id: value.slice(separator + 1) }
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

/** Every meter, of every fuel, whose location is `locationId`. */
export function metersAtLocation(
    locationId,
    { electricity = [], gas = [], water = [] }
) {
    const at = (meters, type) =>
        meters
            .filter((meter) => sameId(meter.location?.id, locationId))
            .map((meter) => ({ type, meter }))
    return [
        ...at(electricity, 'electricity-meter'),
        ...at(gas, 'gas-meter'),
        ...at(water, 'water-meter')
    ]
}

/** Locations whose parent is `locationId` (houses sharing a parent's meter). */
export function childLocations(locations, locationId) {
    return locations.filter((location) =>
        sameId(location.parent?.id, locationId)
    )
}

/** Meters that list `meterId` as their parent (sub-meters). */
export function subMeters(meters, meterId) {
    return meters.filter((meter) => sameId(meter.parent?.id, meterId))
}

/** A meter's name as each table shows it. */
export function meterName(type, meter) {
    if (!meter) return ''
    if (type === 'electricity-meter') {
        return meter.locationRef || meter.meterNumber || 'Electricity meter'
    }
    if (type === 'gas-meter') {
        return meter.mprn || meter.meter_serial_number || 'Gas meter'
    }
    return meter.spid || meter.meter_serial_number || 'Water meter'
}

/** The inclusive `[start, end]` ISO dates for the `days` ending yesterday. */
export function usageWindow(days, today = DateTime.now()) {
    const end = today.startOf('day').minus({ days: 1 })
    const start = end.minus({ days: days - 1 })
    return { start: start.toISODate(), end: end.toISODate() }
}

/**
 * One value per calendar day of the window — `null` where no reading arrived,
 * so a gap is shown as a gap rather than silently read as zero use.
 */
export function summariseDailyUsage(readings, { start, end }) {
    const byDate = new Map()
    for (const reading of readings ?? []) {
        const date = String(reading.date).slice(0, 10)
        if (reading.incremental == null) continue
        byDate.set(date, (byDate.get(date) ?? 0) + Number(reading.incremental))
    }

    const days = []
    const last = DateTime.fromISO(end)
    for (
        let day = DateTime.fromISO(start);
        day <= last;
        day = day.plus({ days: 1 })
    ) {
        const date = day.toISODate()
        days.push({ date, value: byDate.has(date) ? byDate.get(date) : null })
    }

    const present = days.filter((day) => day.value !== null)
    const total = present.reduce((sum, day) => sum + day.value, 0)
    return {
        days,
        total,
        average: present.length > 0 ? total / present.length : null,
        missingDays: days.length - present.length,
        max: present.reduce((max, day) => Math.max(max, day.value), 0)
    }
}

const plainNumber = (value, digits) =>
    new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits }).format(
        value
    )

/**
 * "1,284 kWh" / "12.9k kWh" / "1.2M kWh". Compacted by hand rather than with
 * Intl's compact notation, whose casing ("K" vs "k") differs between runtimes.
 */
export function formatKwh(value) {
    if (value == null) return '—'
    const size = Math.abs(value)
    if (size >= 1e6) return `${plainNumber(value / 1e6, 1)}M kWh`
    if (size >= 1e4) return `${plainNumber(value / 1e3, 1)}k kWh`
    return `${plainNumber(value, size < 10 ? 1 : 0)} kWh`
}
